import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  TemplateRef,
  computed,
  effect,
  inject,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatDialog } from '@angular/material/dialog';
import { MatProgressBar } from '@angular/material/progress-bar';
import { Router } from '@angular/router';
import { Actions, ofType } from '@ngrx/effects';
import { Store, select } from '@ngrx/store';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { ToastrService } from 'ngx-toastr';
import {
  ConfirmDialogModel,
  ConfirmDialogService,
  UiDatasetTableComponent,
  UiPreloaderComponent,
  PreloaderColor,
  mapToUiUserSettings$,
} from '@ui';
import { MergedColumnContext } from '@ui/lib/ui-kit/ui-dataset-table';
import {
  combineLatest,
  filter,
  firstValueFrom,
  lastValueFrom,
  take,
} from 'rxjs';

import {
  ARCHIVE_DEFAULT_DURATION_MIN,
  initialArchive,
} from '@consts/archive.conts';
import { routerSegments } from '@consts/routes';
import { StorageBucket } from '@consts/storage-url.util';
import { ArchiveTimestampLocation } from '@enums/archive.enum';
import { GrantedAccessType } from '@enums/granted-access.enum';
import { AddArchiveModalV2Component } from '@modals/add-archive-modal/add-archive-modal.component';
import { AddArchiveModels } from '@modals/add-archive-modal/models/models';
import {
  Archive,
  ArchiveStatus,
  ArchiveVideoFormat,
} from '@models/archive.model';
import { IntelligenceModels } from '@models/intelligence.model';
import { PermissionModel } from '@models/permission.model';
import { ArchiveActions } from '@states/archive/archive.action-types';
import * as ArchiveSelectors from '@states/archive/archive.selectors';
import * as GrantedAccessActions from '@states/granted-access/granted-access.actions';
import { OrganizationSelectors } from '@states/organization/organization.selector-types';
import { PermissionSelectors } from '@states/permissions/permissions.selector-types';
import * as UserSettingsSelectors from '@states/user-settings/user-settings.selectors';
import moment from 'moment-timezone';

import { RbacPermissionService } from '../../../../../authorization/rbac-permission.service';
import { ArchiveService } from '../../../../../development/archive.service';
import { ThumbnailsService } from '../../../../../development/thumbnails.service';
import {
  getArchiveCloudPreviewUri,
  getArchiveObjectPathFromGsUri,
} from '../../../../../helpers/common.helpers';
import {
  ArchiveCellContext,
  createArchiveTableConfig,
  isTimelapseProcessing,
} from '../../../../../pages/archive/pages/archive-main/archive-main-table.util';
import { StandaloneHlsPlayerModel } from '../../../../../shared/video/standalone-hls-player/standalone-hls-player.model';
import { StandaloneHlsPlayerService } from '../../../../../shared/video/standalone-hls-player/standalone-hls-player.service';
import { ArchiveAddToCaseDialogComponent } from '../../../archive-add-to-case-dialog/archive-add-to-case-dialog.component';
import { ShareAccessComponent } from '../../../share-access/share-access.component';
import { VideoPreviewModalComponent } from '../../../video-preview-modal/video-preview-modal.component';
import { VideoSignatureModalComponent } from '../../../video-signature-modal/video-signature-modal.component';
import { IntelligenceResultsService } from '../../../../../services/intelligence-results.service';
import { IntelligenceToolsService } from '../../../../../services/intelligence-tools.service';
import { AuthDownloadService } from '../../../../services/auth-download.service';
import { createArchiveDownloadCallbacks } from '../../../../services/archive-download-feedback.util';

type ArchiveDocument = Archive.ArchiveDocument;

@Component({
  selector: 'app-archive-result',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    TranslatePipe,
    UiDatasetTableComponent,
    UiPreloaderComponent,
    MatProgressBar,
  ],
  templateUrl: './archive-result.component.html',
  styleUrls: ['./archive-result.component.scss'],
})
export class ArchiveResultComponent {
  private readonly resultsService = inject(IntelligenceResultsService);
  private readonly toolsService = inject(IntelligenceToolsService);
  private readonly store = inject(Store);
  private readonly dialog = inject(MatDialog);
  private readonly router = inject(Router);
  private readonly archiveService = inject(ArchiveService);
  private readonly thumbnailsService = inject(ThumbnailsService);
  private readonly hlsPlayer = inject(StandaloneHlsPlayerService);
  private readonly confirmDialog = inject(ConfirmDialogService);
  private readonly translate = inject(TranslateService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly rbac = inject(RbacPermissionService);
  private readonly actions$ = inject(Actions);
  private readonly authDownloadService = inject(AuthDownloadService);
  private readonly toastrService = inject(ToastrService);

  private readonly nameWithStatusCell =
    viewChild.required<TemplateRef<MergedColumnContext<ArchiveDocument>>>(
      'nameWithStatusCell',
    );
  private readonly typeCell =
    viewChild.required<TemplateRef<ArchiveCellContext>>('typeCell');
  private readonly cameraCell =
    viewChild.required<TemplateRef<ArchiveCellContext>>('cameraCell');
  private readonly sharedCell =
    viewChild.required<TemplateRef<ArchiveCellContext>>('sharedCell');
  private readonly statusCell =
    viewChild.required<TemplateRef<ArchiveCellContext>>('statusCell');
  private readonly downloadProgress = this.store.selectSignal(
    ArchiveSelectors.selectDownloadProgress,
  );
  private readonly userSettings$ = mapToUiUserSettings$(
    this.store.select(UserSettingsSelectors.selectSelectedUserSettings),
  );
  private readonly canRead = this.rbac.createChecker([
    PermissionModel.Permissions.ArchiveRead,
  ]);
  private readonly canShare = this.rbac.createChecker([
    PermissionModel.Permissions.ArchiveShare,
  ]);
  private readonly canDownload = this.rbac.createChecker([
    PermissionModel.Permissions.ArchiveDownload,
  ]);
  private readonly canDelete = this.rbac.createChecker([
    PermissionModel.Permissions.ArchiveDelete,
  ]);
  private readonly canCreate = this.rbac.createChecker([
    PermissionModel.Permissions.ArchiveCreate,
  ]);
  private readonly canManageCases = this.rbac.createChecker([
    PermissionModel.Permissions.CaseManage,
  ]);

  protected readonly ArchiveVideoFormat = ArchiveVideoFormat;
  protected readonly preloaderColor = PreloaderColor;
  protected readonly archiveStatusLookup = Archive.archiveStatusLookup;
  /**
   * Shared with `archive-main.component` so this status column and the archives page can never
   * drift from the row-click guard applied inside `createArchiveTableConfig`.
   */
  protected readonly isTimelapseProcessing: (row: ArchiveDocument) => boolean =
    isTimelapseProcessing;

  protected readonly response =
    computed<IntelligenceModels.Archive.Response | null>(() => {
      const result = this.resultsService.current();
      return result?.type === 'archive'
        ? (result.data as IntelligenceModels.Archive.Response)
        : null;
    });
  protected readonly archives = computed<ArchiveDocument[]>(
    () => this.response()?.archives ?? [],
  );
  protected readonly tableConfig = computed(() =>
    createArchiveTableConfig({
      templates: {
        nameWithStatusCell: this.nameWithStatusCell(),
        typeCell: this.typeCell(),
        cameraCell: this.cameraCell(),
        sharedCell: this.sharedCell(),
        statusCell: this.statusCell(),
      },
      downloadProgress: this.downloadProgress(),
      permissions: {
        canRead: this.canRead(),
        canShare: this.canShare(),
        canDownload: this.canDownload(),
        canDelete: this.canDelete(),
        canCreate: this.canCreate(),
        canManageCases: this.canManageCases(),
      },
      archiveStatus: ArchiveStatus,
      archiveStatusLookup: Archive.archiveStatusLookup,
      ArchiveVideoFormat,
      userSettings$: this.userSettings$,
      onViewArchive: (row: ArchiveDocument) => this.viewArchive(row),
    }),
  );

  public constructor() {
    // The downloadArchive effect only resolves the URL and dispatches success;
    // the component is responsible for actually triggering the browser download.
    this.actions$
      .pipe(
        ofType(ArchiveActions.downloadArchiveSuccess),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(({ url, name }) => this.triggerDownload(url, name));

    effect(() => {
      const response = this.response();
      if (!response) {
        this.resultsService.setHeaderActions([]);
        return;
      }
      const actions: IntelligenceModels.HeaderAction[] = [
        {
          kind: IntelligenceModels.HeaderActionKind.ICON,
          icon: 'ui-icon-new-tab',
          tooltip: 'SEARCH_GLOBAL_MODAL.ARCHIVE.OPEN_ARCHIVES',
          onClick: () => this.openArchivesPage(),
        },
      ];
      if (this.canCreate()) {
        actions.push(
          {
            kind: IntelligenceModels.HeaderActionKind.BUTTON_DEFAULT,
            label: 'ARCHIVE.CREATE_TIMELAPSE',
            tooltip: 'ARCHIVE.CREATE_TIMELAPSE',
            onClick: () => {
              void this.addTimelapse();
            },
          },
          {
            kind: IntelligenceModels.HeaderActionKind.BUTTON_PRIMARY,
            label: 'ARCHIVE.CREATE_ARCHIVE',
            tooltip: 'ARCHIVE.CREATE_ARCHIVE',
            onClick: () => {
              void this.addArchive();
            },
          },
        );
      }
      this.resultsService.setHeaderActions(actions);
    });
    this.destroyRef.onDestroy(() => this.resultsService.setHeaderActions([]));
  }

  protected statusIcon(item: ArchiveDocument): string | null {
    if (
      item.status === ArchiveStatus.COMPLETED ||
      item.status === ArchiveStatus.SUBMITTING_TRANSCODE ||
      item.status === ArchiveStatus.PROCESSING_TRANSCODE ||
      item.status === ArchiveStatus.COMPLETED_TRANSCODE
    ) {
      return 'ui-icon-status-online';
    }
    if (
      item.status === ArchiveStatus.ERROR ||
      item.status === ArchiveStatus.FAILED_TRANSCODE
    ) {
      return 'ui-icon-info';
    }
    return null;
  }

  protected statusLabel(item: ArchiveDocument): string {
    return Archive.archiveStatusLookup[item.status] ?? '';
  }

  protected isProcessing(item: ArchiveDocument): boolean {
    return item.status === ArchiveStatus.UPLOADING;
  }

  protected isHighlighted(item: ArchiveDocument): boolean {
    return (
      !!item.sessionId &&
      item.sessionId === this.response()?.highlightedSessionId
    );
  }

  protected onRowAction(event: {
    actionId: string;
    row: ArchiveDocument;
  }): void {
    const { actionId, row } = event;
    // Note: there is no 'view' action - opening the preview is a row click, handled by
    // `viewArchive` through the table config's `onRowClick`.
    switch (actionId) {
      case 'share':
        this.openShare(row);
        break;
      case 'download':
        this.store.dispatch(
          ArchiveActions.downloadArchive({ archive: row, sharedToken: false }),
        );
        break;
      case 'video-signature':
        this.openVideoSignature(row);
        break;
      case 'retry':
        this.retry(row);
        break;
      case 'add-to-case':
        this.openAddToCase(row);
        break;
      case 'delete':
        this.delete(row);
        break;
    }
  }

  protected loadMore(): void {
    void this.fetchMore();
  }

  private triggerDownload(url: string, name: string): void {
    this.authDownloadService.download(
      url,
      name,
      createArchiveDownloadCallbacks(this.toastrService, this.translate),
    );
  }

  private openArchivesPage(): void {
    const path = this.router.serializeUrl(
      this.router.createUrlTree([
        '/',
        routerSegments.archive,
        routerSegments.main,
      ]),
    );
    window.open(
      window.location.origin ? `${window.location.origin}${path}` : path,
      '_blank',
      'noopener,noreferrer',
    );
  }

  private openShare(archive: ArchiveDocument): void {
    this.dialog.open(ShareAccessComponent, {
      panelClass: 'thumbnail-dialog-wrapper',
      width: '502px',
      maxHeight: '802px',
      data: {
        title: 'archive',
        name: archive.name,
        entityId: archive._id,
        type: GrantedAccessType.ARCHIVE,
      },
    });
  }

  private openAddToCase(archive: ArchiveDocument): void {
    this.dialog.open(ArchiveAddToCaseDialogComponent, {
      width: '420px',
      panelClass: 'modal-no-padding',
      disableClose: true,
      data: { archive },
    });
  }

  private delete(archive: ArchiveDocument): void {
    if (!archive.sessionId) return;
    this.confirmDialog
      .open({
        type: ConfirmDialogModel.ConfirmDialogType.CONFIRM,
        title: this.translate.instant('CONFIRM_MODALS.DELETE_ARCHIVE.TITLE'),
        msg: this.translate.instant('CONFIRM_MODALS.COMMON_MSG', {
          name: archive.name,
        }),
        confirm: this.translate.instant('CONFIRM_MODALS.COMMON_YES'),
        cancel: this.translate.instant('COMMON.CANCEL'),
        disableClose: true,
      })
      .pipe(
        take(1),
        filter(
          (result) => result?.selection === ConfirmDialogModel.ConfirmResult.OK,
        ),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => {
        this.store.dispatch(
          ArchiveActions.deleteArchive({ sessionId: archive.sessionId }),
        );
      });
  }

  private retry(archive: ArchiveDocument): void {
    if (archive.status === ArchiveStatus.FAILED_TRANSCODE) {
      if (archive.sessionId) {
        this.store.dispatch(
          ArchiveActions.retryTranscode({ sessionId: archive.sessionId }),
        );
      }
      return;
    }
    this.store.dispatch(ArchiveActions.retry({ archive }));
  }

  private openVideoSignature(archive: ArchiveDocument): void {
    this.dialog.open(VideoSignatureModalComponent, {
      panelClass: 'modal-no-padding',
      width: '556px',
      data: {
        signature: archive.md5Sum,
        videos: archive.files?.videos,
      },
    });
  }

  /**
   * Opens the preview player for a row click. The table only calls this for rows that pass
   * `isArchiveViewable`, but the player chain is still async - surface failures instead of
   * dropping them as an unhandled rejection.
   */
  private viewArchive(archive: ArchiveDocument): void {
    this.openPlayback(archive).catch(() => {
      this.toastrService.error(
        this.translate.instant('PLAYBACK_ERRORS.GENERIC'),
      );
    });
  }

  private async openPlayback(archive: ArchiveDocument): Promise<void> {
    if (
      archive.videoFormat === ArchiveVideoFormat.HLS ||
      archive.videoFormat === ArchiveVideoFormat.TimelapseMP4
    ) {
      await this.openHlsPlayback(archive);
      return;
    }
    this.dialog.open(VideoPreviewModalComponent, {
      width: '80vh',
      disableClose: true,
      data: {
        url: this.getCloudPreviewUri(archive),
        cameraName: archive.cameraName ?? '',
        locationName: archive.name,
        timestamp: archive.start * 1000,
        objectUrls: archive.objectUrls ?? [],
      },
    });
  }

  private async openHlsPlayback(archive: ArchiveDocument): Promise<void> {
    if (archive.videoFormat === ArchiveVideoFormat.TimelapseMP4) {
      const video = archive.files?.videos?.[0];
      const path = getArchiveObjectPathFromGsUri(video?.hlsPath ?? '');
      const url = `${this.thumbnailsService.getBucketBaseUrl(StorageBucket.Archives, true, false, archive.edgeId)}/${path}`;
      const timeline: StandaloneHlsPlayerModel.TimelineData = {
        smartStorage: [],
        regularStorage: [
          {
            start: video?.start,
            end: video?.end,
            url,
          },
        ],
      };
      this.hlsPlayer.openPlayerWithTimelapse(
        timeline,
        archive.name,
        video?.timelapseMetrics,
        archive.timelapse?.duration,
        archive.objectUrls ?? [],
      );
      return;
    }

    const json = await lastValueFrom(
      this.archiveService.getArchiveTimeline(archive),
    );
    const timeline = this.hlsPlayer.createTimelineDataFromJson(json);
    this.hlsPlayer.openPlayer(
      timeline,
      archive.name,
      archive.cameraId,
      archive.objectUrls ?? [],
    );
  }

  private getCloudPreviewUri(archive: ArchiveDocument): string {
    if (archive.isClip) {
      const baseUrl =
        `${this.thumbnailsService.getBucketBaseUrl(StorageBucket.Videos, true, false, archive.edgeId)}` +
        `/org/${archive.orgId}/edge/${archive.edgeId}/camera/${archive.cameraId}` +
        `/session/${archive.sessionId}/preview`;
      return `${baseUrl}/alert_${archive.sessionId?.split('alerts/')[1]}.mp4`;
    }
    return getArchiveCloudPreviewUri(archive);
  }

  private async addArchive(): Promise<void> {
    this.store.dispatch(GrantedAccessActions.resetGrantedAccess());
    const [hasOrgEditPermission, durationLimit] = await firstValueFrom(
      combineLatest([
        this.store.pipe(
          select(
            PermissionSelectors.checkAccessPermissionsOR(
              [
                PermissionModel.Permissions
                  .OrganizationSettingsEditOrganization,
              ],
              [],
            ),
          ),
        ),
        this.store.pipe(select(OrganizationSelectors.selectDurationLimit)),
      ]).pipe(take(1)),
    );
    const durationMinutes = Math.min(
      this.getDurationInMinutes(durationLimit),
      ARCHIVE_DEFAULT_DURATION_MIN,
    );
    const data: AddArchiveModels.AddArchiveModalDialogOptions = {
      options: {
        limitDuration: durationLimit,
        hasOrgEditPermission,
        archiveModalConfig: {
          hasSmartStorage: false,
          disableSelectedCamera: false,
          disableSelectDateRange: false,
          smartStorageInterval: null,
        },
      },
      selectedValue: {
        ...initialArchive,
        name: this.translate.instant(initialArchive.name),
        start: moment().subtract(durationMinutes, 'minutes').toString(),
        end: moment().toString(),
      },
    };
    this.dialog.open(AddArchiveModalV2Component, {
      width: '502px',
      panelClass: 'modal-no-padding',
      disableClose: true,
      data,
    });
  }

  private async addTimelapse(): Promise<void> {
    this.store.dispatch(GrantedAccessActions.resetGrantedAccess());
    const { AddTimelapseModalComponent } =
      await import('@modals/add-timelapse-modal/add-timelapse-modal.component');
    this.dialog.open(AddTimelapseModalComponent, {
      width: '502px',
      panelClass: 'modal-no-padding',
      disableClose: true,
      data: {
        options: {
          timelapseModalConfig: {
            disableSelectedCamera: false,
            disableSelectTimeframe: false,
          },
        },
        selectedValue: {
          name: null,
          selectedCamera: null,
          timeframe: null,
          duration: 1,
          timestampLocation: ArchiveTimestampLocation.none,
          cameraNameAndLocation: ArchiveTimestampLocation.none,
        },
      },
    });
  }

  private getDurationInMinutes(limit: string): number {
    const [hours, minutes] = limit.split(':').map(Number);
    return (hours || 0) * 60 + (minutes || 0);
  }

  private async fetchMore(): Promise<void> {
    const response = this.response();
    const archives = this.archives();
    if (!response?.hasMore || !archives.length) return;
    const cursor = archives[archives.length - 1].createdAt;
    if (!cursor) return;
    const page = await this.toolsService.fetchArchives({
      limit: response.filter.limit,
      query: response.filter.query ?? undefined,
      latestTs: cursor,
      cameraName: response.filter.cameraName,
      cameraIds: response.filter.cameraIds,
      statuses: response.filter.statuses,
      types: response.filter.types,
      start: response.filter.start,
      end: response.filter.end,
    });
    this.resultsService.appendArchivesToCurrent(
      page.archives,
      cursor,
      !!page.hasMore,
    );
  }
}
